'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/classnames'

type CountUpProps = {
  value: number
  label: string
  /** Animation length in milliseconds. */
  duration?: number
  suffix?: string
  className?: string
  /** Show a short +N beside the number when it grows after the intro animation. */
  showIncrement?: boolean
}

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3)

function formatCount(value: number, language: string) {
  const separator = language === 'en' ? ',' : '.'
  return Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

const CountUp = ({ value, label, duration = 1800, suffix = '', className, showIncrement = false }: CountUpProps) => {
  const { i18n } = useTranslation()
  const [current, setCurrent] = useState(0)
  const [increment, setIncrement] = useState(0)
  const displayed = useRef(0)
  const settled = useRef(false)
  const formattedValue = formatCount(value, i18n.language)
  const formattedCurrent = formatCount(current, i18n.language)

  useEffect(() => {
    const from = displayed.current
    const delta = value - from
    const introDone = settled.current
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const runDuration = introDone ? Math.min(duration, 700) : duration

    if (introDone && showIncrement && delta > 0) setIncrement(delta)

    if (reducedMotion || runDuration <= 0) {
      displayed.current = value
      settled.current = true
      setCurrent(value)
      return
    }

    let frame = 0
    const start = performance.now()

    const tick = () => {
      const progress = Math.min(Math.max((performance.now() - start) / runDuration, 0), 1)
      const next = from + delta * easeOutCubic(progress)
      displayed.current = next
      setCurrent(next)
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
        return
      }
      displayed.current = value
      settled.current = true
      setCurrent(value)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [value, duration, showIncrement])

  useEffect(() => {
    if (!increment) return
    const timeout = window.setTimeout(() => setIncrement(0), 2200)
    return () => window.clearTimeout(timeout)
  }, [increment])

  return (
    <div className={cn('flex flex-col items-center text-center', className)} aria-live='polite' aria-label={`${formattedValue}${suffix} ${label}`}>
      <p
        className={cn(
          'text-xl font-bold leading-none tracking-tight tabular-nums transition-colors duration-700 sm:text-2xl',
          increment > 0 ? 'text-emerald-600' : 'text-blue-950',
        )}
      >
        <span className='relative grid'>
          <span className='invisible col-start-1 row-start-1' aria-hidden='true'>
            {formattedValue}
            {suffix}
          </span>
          <span className='col-start-1 row-start-1' aria-hidden='true'>
            {formattedCurrent}
            {suffix}
          </span>
          {increment > 0 ? (
            <span key={increment} className='absolute bottom-full left-1/2 mb-1 -translate-x-1/2 text-xs font-semibold text-emerald-600 motion-safe:animate-count-bump motion-reduce:animate-none' aria-hidden='true'>
              +{increment}
            </span>
          ) : null}
        </span>
      </p>
      <p className='mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-950/70 sm:text-sm' aria-hidden='true'>
        {label}
      </p>
    </div>
  )
}

export default CountUp
